import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';

// ICONS
import { ReactComponent as TrainingIcon } from '../../assets/svg/training-icon.svg';

// TYPES
import { closeModalType } from '../../components/GlobalModal';
import { DomainTrainingProfileType, TrainingEditCommand } from '../../api/models';

interface AssignAllTrainingModalTypes {
	assignedTrainingIdArray: string[];
	getTrainingData: () => Promise<void>;
	profileType: DomainTrainingProfileType;
	setActiveTab: React.Dispatch<React.SetStateAction<string>>;
	closeModal?: closeModalType;
}

const AssignAllTrainingModal: React.FC<AssignAllTrainingModalTypes> = ({
	assignedTrainingIdArray,
	getTrainingData,
	profileType,
	setActiveTab,
	closeModal
}) => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const confirmAssignAllTraining = async () => {
		setIsLoading(true);

		const data: TrainingEditCommand = {
			moduleIds: assignedTrainingIdArray,
			profileType
		};

		const response = await apibridge.postTrainingEdit(data);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			await getTrainingData();

			setActiveTab(profileType);

			if (closeModal) closeModal();
		}
	};

	return (
		<div className="modal-confirmation">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<figure className="icon">
									<TrainingIcon />
								</figure>

								<h2 className="heading h4 mbtom-20">
									You are about to assign the <strong>all available training modules</strong> to the{' '}
									<span className="text-capitalize">{profileType}</span> group.
								</h2>

								<p className="heading h4">
									Please confirm you have viewed the training module(s) and have determined that its contents are
									appropriate for the group to which it is being assigned.
								</p>

								<section className="actions">
									<Button className="btn-primary full-width" onClick={confirmAssignAllTraining}>
										Assign Training
									</Button>

									<Button className="btn-secondary full-width" onClick={closeModal}>
										Cancel
									</Button>
								</section>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default AssignAllTrainingModal;
